import { Card } from '@/components/ui/card';
import { Typography } from '@/components/ui/typography';
import { formatDate } from '@/util/formatting';
import type { PropertyTable } from '../metadata/propertyConfig';

type CellNode = { data?: unknown; component?: React.ReactNode } | undefined;
const nodeOf = (cell: unknown): React.ReactNode => (cell as CellNode)?.component ?? null;
const dataOf = (cell: unknown): string | null => {
  const d = (cell as CellNode)?.data;
  return d == null ? null : String(d);
};

/**
 * Mobile card for one orders-list row; reuses the row's prebuilt cell
 * components (status pill, service pills, Quick Actions kebab) so table and
 * card stay one source of truth. `eyebrow` (above the address) and `footer`
 * (below the services) are slots for consumers whose table carries columns
 * this card doesn't know about — the cross-account view puts the account
 * name and its Assets action there.
 */
export function OrderCard({
  row,
  eyebrow,
  footer,
}: {
  row: PropertyTable;
  eyebrow?: React.ReactNode;
  footer?: React.ReactNode;
}) {
  const [addressLine1, ...addressRest] = (row.formattedAddress ?? '')
    .split('\n')
    .filter(Boolean);
  const appointment = row['appointments.dateStartLocal'] as string | null | undefined;

  return (
    <Card className="flex flex-col gap-3 border-border p-4" data-testid="order-card">
      {eyebrow}
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <Typography as="p" className="text-base font-semibold leading-6 text-foreground line-clamp-2">
            {addressLine1}
          </Typography>
          {addressRest.map((line) => (
            <Typography key={line} as="p" className="text-sm leading-5 text-muted-foreground line-clamp-1">
              {line}
            </Typography>
          ))}
        </div>
        <div className="shrink-0">{nodeOf(row.moreOption)}</div>
      </div>

      <div className="flex flex-wrap items-center gap-2">{nodeOf(row.orderStatus)}</div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <div>
          <dt className="text-muted-foreground">Order ID</dt>
          <dd className="font-medium text-foreground">{dataOf(row.orderNumber) ?? '-'}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Amount</dt>
          <dd className="font-medium tabular-nums text-foreground">{dataOf(row.orderAmount) ?? '-'}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Order Date</dt>
          <dd className="text-foreground">{row.orderDate ? formatDate(row.orderDate as string) : '-'}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Appointment</dt>
          <dd className="text-foreground">{appointment || '-'}</dd>
        </div>
      </dl>

      {nodeOf(row.servicesOrdered) && (
        <div className="flex flex-wrap items-center gap-1.5">{nodeOf(row.servicesOrdered)}</div>
      )}
      {footer}
    </Card>
  );
}
